/**
 * The Dev Tools window — Shift+P (T73).
 *
 * A place to put the instruments that help build the world but are not part of
 * playing it: the flat-terrain view (moved here out of the HUD, where it was
 * the only control and looked like a game setting), the sun and sky sliders,
 * the graphics isolation switches, and a fuzzy search over every address,
 * landeign, örnefni and building in Iceland.
 *
 * 🔴 **Three things LEFT this window in T187** and are now in the game menu on
 * Escape: the **coordinate box**, the **Perfect day / Winter night /
 * Norðurljós** buttons and the **Rain** switch. 🎯 haugur asked for them where
 * a player can find them — *"move the go to coordineat from Dev tools to main
 * tab"*. ⚠️ **They were moved, not copied.** The sliders here and those buttons
 * change the same `LightSettings`, which is why it lives in `settings.ts` and
 * neither window owns it — see that file's header.
 *
 * ⚠️ **It searches the whole country; the world is 1,400 × 1,150 m of
 * Hafnarfjörður.** Results say which side of that line they fall on, and
 * teleporting outside is allowed — see `search.ts`. Choosing where the world
 * should move to (T62) is a decision that needs somewhere to stand and look.
 */

/* ⚠️ No `isTypingTarget` here, deliberately — unlike every other key handler
   in the client. Shift+P and Escape must work *while* the search box has
   focus, or the window cannot be closed from inside the box you are typing
   in. The other handlers guard; this one is the exception that makes the
   guarding safe. */
import { inWorld, northingToZ } from '@lofi/shared';
import {
  DEFAULT_GRAPHICS,
  DEFAULT_LIGHT,
  TONE_MAPPINGS,
  lightStore,
  type GraphicsSettings,
  type LightSettings,
  type ToneMappingName,
} from './settings.ts';

/* 🔴 Re-exported because `main.ts` has always imported these two from here and
   nothing about where they LIVE is `main.ts`'s business. ⚠️ The values moved
   to `settings.ts` in T187 so the game menu and this window could share one
   copy — see that file's header for why two copies would have been a bug. */
export {
  DEFAULT_GRAPHICS,
  DEFAULT_LIGHT,
  TONE_MAPPINGS,
  type GraphicsSettings,
  type LightSettings,
  type ToneMappingName,
};

export interface SearchHit {
  readonly kind: 'address' | 'parcel' | 'placename' | 'structure';
  readonly label: string;
  readonly detail: string;
  /**
   * The postal town — "Hafnarfjörður", "Siglufjörður".
   *
   * ⚠️ Without it there is no way to tell two "Hverfisgata 3" apart, and
   * Iceland has eleven places called Hamarinn.
   */
  readonly town: string;
  readonly refs: string;
  /** ISN93 easting. */
  readonly x: number;
  /** ISN93 northing — converted to world z by the caller. */
  readonly y: number;
  readonly inWorld: boolean;
}

/** What a search may be narrowed to. `null` is everything. */
export type SearchKind = SearchHit['kind'];

/**
 * The filter buttons, in the order they appear.
 *
 * ⚠️ Addresses first and immediately after "All", because that is the one
 * haugur reached for: searching "Urðarstígur 10" returns the address, the
 * house and the garage — three rows for one place — and most of the time the
 * address is the only one wanted.
 */
const KIND_FILTERS: readonly { kind: SearchKind | null; label: string }[] = [
  { kind: null, label: 'Allt' },
  { kind: 'address', label: 'Heimilisföng' },
  { kind: 'structure', label: 'Byggingar' },
  { kind: 'parcel', label: 'Landeignir' },
  { kind: 'placename', label: 'Örnefni' },
];

/** One light slider, and how to read and label the value it controls. */
interface LightSlider {
  readonly input: HTMLInputElement;
  readonly value: HTMLSpanElement;
  readonly read: (l: LightSettings) => number;
  readonly format: (v: number) => string;
}

/** The highest the sun ever gets at Hafnarfjörður: 90° − 64.07°N + 23.44°. */
export const ICELAND_MAX_SUN_ELEVATION = 49.4;

export interface DevToolsHandlers {
  /** Switch the flat debug view on or off (T49). */
  onToggleFlat(): void;
  /* ⚠️ `onLightChange` was removed in T187. The light is applied by a
     subscription to `lightStore` in `main.ts`, so that a change made HERE and
     a change made in the game menu reach the renderer by one path. */
  /* ⚠️ `onGraphicsChange` was removed in T188, for the same reason as
     `onLightChange`: the graphics controls moved to the game menu's Graphics
     tab, and `main.ts` subscribes to `graphicsStore`. */
  /**
   * Go to an ISN93 position.
   *
   * @param x easting, @param y **northing** — not world z. The conversion is
   *   the caller's, and is done in exactly one place.
   */
  onTeleport(x: number, y: number, label: string): void;
  /**
   * Run a search. Returns hits, or throws — the panel shows either.
   *
   * @param kind narrow to one kind, or `null` for all. ⚠️ Passed to the
   *   server rather than applied here: the row limit is applied after the
   *   filter, so "addresses only" means twenty addresses and not however many
   *   addresses survived a mixed cut.
   */
  search(query: string, kind: SearchKind | null): Promise<SearchHit[]>;
}

/**
 * How long to wait after a keystroke before searching.
 *
 * Long enough that typing a street name is one query rather than eleven, short
 * enough to feel like it is keeping up. The query itself measures 13–73 ms
 * against the real index, so this is the dominant term either way.
 */
const SEARCH_DEBOUNCE_MS = 180;

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  address: 'heimilisfang',
  parcel: 'landeign',
  placename: 'örnefni',
  structure: 'bygging',
};

export class DevTools {
  private readonly root: HTMLDivElement;
  private readonly flatSwitch: HTMLButtonElement;
  private readonly searchInput: HTMLInputElement;
  private readonly resultList: HTMLDivElement;
  private readonly handlers: DevToolsHandlers;

  private readonly filterRow: HTMLDivElement;
  private readonly filterButtons = new Map<string, HTMLButtonElement>();

  /**
   * Light controls (T84). Value labels are redrawn as the sliders move.
   *
   * Each one carries its own reader rather than a key into `LightSettings`,
   * so nothing indexes the settings object by a variable — which is both
   * safer and what `security/detect-object-injection` is asking for.
   */
  private readonly lightSliders: LightSlider[] = [];
  private readonly sunNote: HTMLDivElement;
  /** Clock-driven sun, or the sliders' (T103 piece ②). */
  private readonly followClockSwitch: HTMLButtonElement;
  private readonly clearSkySwitch: HTMLButtonElement;
  /* The four folds. ⚠️ Declared as fields because the constructor fills them
     out of order — "Go to a coordinate" is built long after World is created. */
  private readonly fold_world = foldBody();
  private readonly fold_sky = foldBody();
  private readonly fold_about = foldBody();
  private readonly folds: { header: HTMLButtonElement; body: HTMLElement }[] = [];
  private readonly copyButton: HTMLButtonElement;
  private light: LightSettings = lightStore.get();
  private readonly rendererNote: HTMLDivElement;
  /* The T94 isolation switches. Named rather than held in an array, so nothing
     indexes a list by a variable — same reason as `lightSliders`. */

  private open = false;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  /** Bumped per search so a slow earlier one cannot overwrite a later one. */
  private searchGeneration = 0;
  /** Which kind the results are narrowed to. `null` is all of them. */
  private kind: SearchKind | null = null;

  constructor(handlers: DevToolsHandlers) {
    this.handlers = handlers;

    this.root = document.createElement('div');
    this.root.className = 'devtools';
    this.root.style.display = 'none';
    /* Announced rather than merely visible: this window steals the keyboard,
       and a screen reader should say so. */
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Dev Tools');

    const title = document.createElement('div');
    title.className = 'devtools-title';
    title.textContent = 'Dev Tools';

    const close = document.createElement('button');
    close.className = 'devtools-close';
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', 'Close Dev Tools');
    close.addEventListener('click', () => this.setOpen(false));
    title.appendChild(close);
    this.root.appendChild(title);

    /* The folds, built in haugur's order. ⚠️ Created BEFORE anything is put in
       them, because the whole panel below appends into these rather than into
       the root. */
    this.root.appendChild(this.foldHeader('World', this.fold_world));
    this.root.appendChild(this.fold_world);
    this.root.appendChild(this.foldHeader('Sky & light', this.fold_sky));
    this.root.appendChild(this.fold_sky);
    this.root.appendChild(this.foldHeader('About', this.fold_about));
    this.root.appendChild(this.fold_about);

    // ── the flat-terrain view, moved here from the HUD ──────────────────────
    this.fold_world.appendChild(section('Terrain'));
    this.flatSwitch = document.createElement('button');
    this.flatSwitch.className = 'switch';
    this.flatSwitch.type = 'button';
    this.flatSwitch.addEventListener('click', (e) => {
      e.stopPropagation();
      handlers.onToggleFlat();
    });
    this.fold_world.appendChild(this.flatSwitch);
    this.setFlatSwitch(false, false);

    // ── light (T84) ─────────────────────────────────────────────────────────
    this.fold_sky.appendChild(section('Light'));

    this.fold_sky.appendChild(
      this.slider(
        'Sun',
        { min: 0, max: 10, step: 0.05 },
        (l) => l.sun,
        (v) => ({ ...this.light, sun: v }),
        (v) => v.toFixed(2),
      ),
    );
    this.fold_sky.appendChild(
      this.slider(
        'Sky light',
        { min: 0, max: 6, step: 0.05 },
        (l) => l.ambient,
        (v) => ({ ...this.light, ambient: v }),
        (v) => v.toFixed(2),
      ),
    );
    this.fold_sky.appendChild(
      this.slider(
        'Sun height',
        /* ⚠️ Goes BELOW the horizon on purpose (T116). With the sun switch off
           this is a time-of-day dial, and −20° is the only practical way to look
           at the night sky without playing for thirty-eight hours. */
        { min: -30, max: 70, step: 0.5 },
        (l) => l.elevation,
        (v) => ({ ...this.light, elevation: v }),
        (v) => `${v.toFixed(1)}°`,
      ),
    );
    this.fold_sky.appendChild(
      this.slider(
        'Sun bearing',
        { min: 0, max: 359, step: 1 },
        (l) => l.azimuth,
        (v) => ({ ...this.light, azimuth: v }),
        (v) => `${v.toFixed(0)}° ${compass(v)}`,
      ),
    );
    this.fold_sky.appendChild(
      this.slider(
        'Exposure',
        { min: 0.2, max: 2, step: 0.05 },
        (l) => l.exposure,
        (v) => ({ ...this.light, exposure: v }),
        (v) => v.toFixed(2),
      ),
    );
    /**
     * 🌕 How dark a moonless night may get (**D60**).
     *
     * ⚠️ **Zero is the shipped default and the honest one.** The slider is here
     * because *"dark means dark"* is a judgement that has to be made by standing
     * in it — and because there is nothing to carry yet, so a genuinely black
     * night is currently a night you can do nothing in. **Fire is T110.**
     */
    this.fold_sky.appendChild(
      this.slider(
        'Night floor',
        { min: 0, max: 0.4, step: 0.01 },
        (l) => l.nightFloor,
        (v) => ({ ...this.light, nightFloor: v }),
        (v) => (v === 0 ? 'dark' : v.toFixed(2)),
      ),
    );

    /**
     * The clock's sun, or the sliders' (T103 piece ②).
     *
     * ⚠️ **Placed directly under the three sliders it overrides**, because a
     * switch that silently disables a control somewhere else on the panel is
     * how somebody spends ten minutes dragging a slider that does nothing.
     */
    this.followClockSwitch = this.crawlSwitch(
      'Sun',
      () => {
        this.applyLight({ ...this.light, followClock: !this.light.followClock });
      },
      this.fold_sky,
    );
    /* 🔴 Labelled HERE as well as in `applyLight`, and the reason is a bug
       haugur reported from a screenshot on 2026-08-11: `applyLight` only runs
       when a setting CHANGES, so on a fresh panel this button sat there as a
       blank bar — a control nobody could see, name or discover. Every other
       switch on the panel is labelled by a `show…` call that runs at startup;
       this one was not. */
    this.showFollowClock(this.light);

    /**
     * 🌟 Pretend the sky is clear (T116).
     *
     * ⚠️ **Directly under the sun switch**, because the two are used together:
     * drag the height to −20° for night, then turn this on to see the stars
     * through Iceland's habitual overcast.
     */
    this.clearSkySwitch = this.crawlSwitch(
      'Sky',
      () => {
        this.applyLight({ ...this.light, clearSky: !this.light.clearSky });
      },
      this.fold_sky,
    );
    this.showClearSky(this.light);

    /* ⚠️ Says in words when the sun is somewhere it cannot be, rather than
       only colouring the number (Design/00 §4, V7). */
    this.sunNote = document.createElement('div');
    this.sunNote.className = 'devtools-note';
    this.fold_sky.appendChild(this.sunNote);

    const toneRow = document.createElement('div');
    toneRow.className = 'devtools-row';

    const toneLabel = document.createElement('label');
    toneLabel.className = 'devtools-slider-label';
    toneLabel.textContent = 'Tone map';
    const toneSelect = document.createElement('select');
    toneSelect.className = 'devtools-select';
    toneSelect.setAttribute('aria-label', 'Tone mapping curve');
    for (const { name, label } of TONE_MAPPINGS) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = label;
      toneSelect.appendChild(option);
    }
    toneSelect.value = this.light.toneMapping;
    toneSelect.addEventListener('change', () => {
      this.applyLight({ ...this.light, toneMapping: toneSelect.value as ToneMappingName });
    });

    const skyInput = document.createElement('input');
    skyInput.type = 'color';
    skyInput.className = 'devtools-colour';
    skyInput.value = this.light.sky;
    skyInput.setAttribute('aria-label', 'Sky colour');
    skyInput.addEventListener('input', () => {
      this.applyLight({ ...this.light, sky: skyInput.value });
    });

    toneRow.append(toneLabel, toneSelect, skyInput);
    this.fold_sky.appendChild(toneRow);

    /* The point of the whole panel: haugur finds numbers by eye, then pastes
       them back so they can be committed. Without this the good result stays
       in his browser and dies with the tab. */
    const lightButtons = document.createElement('div');
    lightButtons.className = 'devtools-row';

    this.copyButton = document.createElement('button');
    this.copyButton.className = 'devtools-go';
    this.copyButton.type = 'button';
    this.copyButton.textContent = 'Copy settings';
    this.copyButton.addEventListener('click', () => void this.copyLight());

    const resetButton = document.createElement('button');
    resetButton.className = 'devtools-go';
    resetButton.type = 'button';
    resetButton.textContent = 'Reset';
    resetButton.addEventListener('click', () => {
      this.applyLight(DEFAULT_LIGHT);
      toneSelect.value = DEFAULT_LIGHT.toneMapping;
      skyInput.value = DEFAULT_LIGHT.sky;
      this.syncSliders();
    });

    lightButtons.append(this.copyButton, resetButton);
    this.fold_sky.appendChild(lightButtons);

    /* ⚠️ What the browser actually chose, not what it could have chosen. The
       same build looked right on a Mac and wrong on two Windows machines — one
       with a dedicated RTX, one with no GPU at all — and a real GPU agreeing
       with a software rasteriser means the difference is the code path, not the
       hardware. Brief §8 asked for this readout before anyone diagnoses a
       rendering problem; it took a whole session to notice. */
    this.rendererNote = document.createElement('div');
    this.rendererNote.className = 'devtools-note';
    this.rendererNote.textContent = 'Renderer: asking…';
    this.fold_about.appendChild(this.rendererNote);

    // ── search ──────────────────────────────────────────────────────────────
    this.fold_world.appendChild(section('Search Iceland'));

    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.className = 'devtools-search';
    this.searchInput.placeholder = 'address · landnúmer · örnefni · building…';
    this.searchInput.setAttribute('aria-label', 'Search addresses, land parcels, placenames');
    this.searchInput.addEventListener('input', () => this.queueSearch());
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        this.runSearch();
      }
    });
    this.fold_world.appendChild(this.searchInput);

    /* The kind filter. Buttons rather than a <select> so the current one is
       visible without opening anything — you are looking at a result list and
       need to know what it is a list *of*. */
    this.filterRow = document.createElement('div');
    this.filterRow.className = 'devtools-filters';
    for (const { kind, label } of KIND_FILTERS) {
      const button = document.createElement('button');
      button.className = 'devtools-filter';
      button.type = 'button';
      button.textContent = label;
      button.addEventListener('click', () => this.setKind(kind));
      this.filterRow.appendChild(button);
      this.filterButtons.set(kind ?? 'all', button);
    }
    this.fold_world.appendChild(this.filterRow);
    this.markActiveFilter();

    this.resultList = document.createElement('div');
    this.resultList.className = 'devtools-results';
    this.fold_world.appendChild(this.resultList);

    const hint = document.createElement('div');
    hint.className = 'devtools-hint';
    hint.textContent =
      'Shift+P closes · searching all Iceland, and the world is only Hafnarfjörður';
    this.fold_world.appendChild(hint);

    document.body.appendChild(this.root);

    /**
     * ⚠️ Capture phase, and `isTypingTarget` everywhere else.
     *
     * Shift+P has to work while the search box has focus, or you cannot close
     * the window you are typing in. Everything else — WASD, F, G, R — must
     * *not* reach the game while typing, which is what `isTypingTarget` in the
     * other handlers is for.
     */
    document.addEventListener('keydown', (e) => {
      if (e.repeat) return;

      /* `e.code` as well as `e.key`: with Shift held `key` is 'P', and on a
         non-US layout `code` is the reliable one. Both, for the same reason
         the render-distance keys check both. */
      if (e.shiftKey && (e.key === 'P' || e.key === 'p' || e.code === 'KeyP')) {
        e.preventDefault();
        this.setOpen(!this.open);
        return;
      }

      /* ⎋ ⚠️ `preventDefault` marks this press as SPOKEN FOR (T187). The
         game menu's Escape ladder in `main.ts` skips an event that is already
         handled; without this, one press would close this window and open the
         menu in the same frame — which reads as Dev Tools turning into it. */
      if (e.key === 'Escape' && this.open) {
        e.preventDefault();
        this.setOpen(false);
      }
    });
  }

  /** Whether the window is showing — the game asks, to suspend its own keys. */
  get isOpen(): boolean {
    return this.open;
  }

  private setOpen(next: boolean): void {
    if (next === this.open) return;
    this.open = next;
    this.root.style.display = next ? 'block' : 'none';

    if (next) {
      /* ⚠️ Release the mouse. Pointer lock hides the cursor, and a window with
         a text box and two buttons is unusable without one. */
      if (document.pointerLockElement) document.exitPointerLock();
      this.searchInput.focus();
      this.searchInput.select();
    }
  }

  /** Label the flat switch with what the world *is* and what pressing it does. */
  private setFlatSwitch(flat: boolean, switching: boolean): void {
    const label = switching
      ? 'Flat terrain · switching…'
      : flat
        ? 'Flat terrain · ON — press to restore'
        : 'Flat terrain · off — press to flatten';
    if (this.flatSwitch.textContent !== label) this.flatSwitch.textContent = label;
    this.flatSwitch.classList.toggle('on', flat);
    this.flatSwitch.disabled = switching;
    this.flatSwitch.setAttribute('aria-pressed', String(flat));
  }

  /** Called every frame by the render loop, like the HUD. */
  render(flat: boolean, switchingFlat: boolean): void {
    if (!this.open) return;
    this.setFlatSwitch(flat, switchingFlat);
  }

  // ── light (T84) ───────────────────────────────────────────────────────────

  /** The settings the panel is currently showing, so the caller can apply them. */
  get lightSettings(): LightSettings {
    return this.light;
  }

  /**
   * One labelled slider, wired straight to `applyLight`.
   *
   * `input` rather than `change`, so the world moves while the handle is being
   * dragged. Finding a light value is a matter of watching it change, not of
   * picking a number and then seeing what happened.
   */
  private slider(
    label: string,
    range: { min: number; max: number; step: number },
    read: (l: LightSettings) => number,
    write: (v: number) => LightSettings,
    format: (v: number) => string,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'devtools-slider';

    const name = document.createElement('label');
    name.className = 'devtools-slider-label';
    name.textContent = label;

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(range.min);
    input.max = String(range.max);
    input.step = String(range.step);
    input.value = String(read(this.light));
    input.setAttribute('aria-label', label);

    const value = document.createElement('span');
    value.className = 'devtools-slider-value';
    value.textContent = format(read(this.light));

    input.addEventListener('input', () => {
      this.applyLight(write(Number(input.value)));
    });

    this.lightSliders.push({ input, value, read, format });

    row.append(name, input, value);
    return row;
  }

  /**
   * Ask for a change to the light.
   *
   * 🔴 **This window no longer owns the light — it asks (T187).** The value,
   * the save and the notify all live in `settings.ts`, because the game menu's
   * *Look at* buttons change the same object these sliders do. Writing it here
   * as well would be the fault this repository has recorded five times: **a
   * second way in that forgets what the first one does.**
   *
   * ⚠️ **Nothing is drawn from here.** The store calls {@link renderLight}
   * back, so a change made in the menu repaints these controls by exactly the
   * same path as a change made on them.
   */
  private applyLight(next: LightSettings): void {
    lightStore.set(next);
  }

  /** Draw the stored light onto this window's controls. */
  private renderLight(next: LightSettings): void {
    this.light = next;

    for (const slider of this.lightSliders) {
      slider.value.textContent = slider.format(slider.read(next));
    }

    this.showFollowClock(next);
    this.showClearSky(next);

    /* ⚠️ The one piece of physics the panel enforces by telling you, not by
       stopping you — the sun above 49.4° is impossible at this latitude, and
       the shipped default is 64.7°. */
    if (next.followClock) {
      this.sunNote.textContent =
        'The sliders are ignored while the clock is driving. Turn the sun to FIXED to tune by hand.';
      this.sunNote.className = 'devtools-note ok';
    } else if (next.elevation > ICELAND_MAX_SUN_ELEVATION) {
      this.sunNote.textContent = `⚠️ ${next.elevation.toFixed(1)}° — impossible at Hafnarfjörður, where the sun never passes ${ICELAND_MAX_SUN_ELEVATION}°`;
      this.sunNote.className = 'devtools-note warn';
    } else if (next.elevation < 5) {
      this.sunNote.textContent = `${next.elevation.toFixed(1)}° — midwinter, near the horizon`;
      this.sunNote.className = 'devtools-note ok';
    } else {
      this.sunNote.textContent = `${next.elevation.toFixed(1)}° — within Iceland's range (max ${ICELAND_MAX_SUN_ELEVATION}°)`;
      this.sunNote.className = 'devtools-note ok';
    }

    /* ⚠️ No save and no `onLightChange` here any more (T187). `settings.ts`
       does both, for every window at once. */
  }

  /** Push stored values back into the controls, after a reset. */
  private syncSliders(): void {
    for (const slider of this.lightSliders) {
      slider.input.value = String(slider.read(this.light));
    }
  }

  /**
   * Put the current settings on the clipboard, in a form that can be pasted
   * into a message and acted on directly.
   */
  private async copyLight(): Promise<void> {
    const l = this.light;
    const text =
      `sun ${l.sun.toFixed(2)} · ambient ${l.ambient.toFixed(2)} · ` +
      `elevation ${l.elevation.toFixed(1)}° · azimuth ${l.azimuth.toFixed(0)}° ${compass(l.azimuth)} · ` +
      `tone ${l.toneMapping} · exposure ${l.exposure.toFixed(2)} · sky ${l.sky.toUpperCase()}`;

    try {
      await navigator.clipboard.writeText(text);
      this.copyButton.textContent = 'Copied';
    } catch {
      /* Clipboard needs a secure context and can be refused. Show the string
         instead — it is short enough to read off the screen. */
      this.sunNote.textContent = text;
      this.sunNote.className = 'devtools-note';
      this.copyButton.textContent = 'Shown below';
    }
    setTimeout(() => (this.copyButton.textContent = 'Copy settings'), 1500);
  }

  /** Say which renderer and GPU the browser picked. Called once, at startup. */
  showRenderer(text: string): void {
    this.rendererNote.textContent = `Renderer: ${text}`;
  }

  /**
   * One of the T94 isolation switches (§ "What is crawling").
   *
   * The label never changes; `showCrawlSwitch` appends ON or OFF to it, so the
   * button says what it *is* as well as what it does.
   *
   * ⚠️ **`into` is required since T188.** It used to default to the graphics
   * fold, and that fold moved to the game menu — a default pointing at a
   * container that no longer exists is exactly the kind of quiet wrong this
   * repo keeps paying for.
   */
  private crawlSwitch(label: string, onClick: () => void, into: HTMLElement): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'switch';
    button.type = 'button';
    button.dataset['label'] = label;
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    into.appendChild(button);
    return button;
  }

  /**
   * Say which of the two is driving the sun, on the button itself (T103 ②).
   *
   * ⚠️ **Called from the constructor as well as from `applyLight`.** The panel
   * only re-renders on a change, so a switch labelled solely on change is a
   * blank bar until somebody happens to touch a slider — which is exactly how
   * this shipped, and haugur spotted it in a screenshot within the hour.
   */
  private showFollowClock(light: LightSettings): void {
    this.followClockSwitch.textContent = light.followClock
      ? 'Sun · FOLLOWS THE CLOCK — height, bearing and sky colour come from the date'
      : 'Sun · FIXED — the three sliders above hold it still';
    this.followClockSwitch.classList.toggle('on', light.followClock);
    this.followClockSwitch.setAttribute('aria-pressed', String(light.followClock));
  }

  /** ⚠️ Labelled at startup for the same reason the sun switch is. */
  private showClearSky(light: LightSettings): void {
    this.clearSkySwitch.textContent = light.clearSky
      ? 'Sky · PRETEND CLEAR — the weather’s cloud is ignored, for looking at the sky'
      : 'Sky · REAL WEATHER — cloud hides the sun, moon and stars';
    this.clearSkySwitch.classList.toggle('on', light.clearSky);
    this.clearSkySwitch.setAttribute('aria-pressed', String(light.clearSky));
  }

  /**
   * A fold's clickable heading (2026-08-11).
   *
   * 🎯 haugur: *"put the tols in categorys that have a folding out arrow… if you
   * have one expanded and open another, the previous fold back in."* **So it is
   * an accordion, not a set of independent folds** — at most one open, and the
   * panel never grows past a screen.
   */
  private foldHeader(title: string, body: HTMLElement): HTMLButtonElement {
    const header = document.createElement('button');
    header.className = 'devtools-foldhead';
    header.type = 'button';
    header.setAttribute('aria-expanded', 'false');
    const arrow = document.createElement('span');
    arrow.className = 'devtools-arrow';
    /* ⚠️ A triangle rotated by CSS, not two different characters — so the state
       is one class and cannot get out of step with `hidden`. */
    arrow.textContent = '▸';
    arrow.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.textContent = title;
    header.append(arrow, label);
    header.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openFold(body.hidden ? body : null);
    });
    this.folds.push({ header, body });
    return header;
  }

  /** Open one fold and shut the rest. `null` shuts them all. */
  private openFold(open: HTMLElement | null): void {
    for (const fold of this.folds) {
      const on = fold.body === open;
      fold.body.hidden = !on;
      fold.header.classList.toggle('open', on);
      fold.header.setAttribute('aria-expanded', String(on));
    }
  }

  /** Put ON/OFF on one isolation switch, and light it when it is on. */
  private showCrawlSwitch(button: HTMLButtonElement, on: boolean): void {
    button.textContent = `${button.dataset['label'] ?? ''} · ${on ? 'ON' : 'OFF'}`;
    button.classList.toggle('on', on);
    button.setAttribute('aria-pressed', String(on));
  }

  // ── search ────────────────────────────────────────────────────────────────

  /** Narrow the results to one kind, or to all of them. */
  private setKind(kind: SearchKind | null): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.markActiveFilter();
    /* Re-run immediately rather than waiting for the debounce: this was a
       click, not a keystroke, and there is nothing more to type. */
    this.runSearch();
  }

  private markActiveFilter(): void {
    for (const [key, button] of this.filterButtons) {
      const active = key === (this.kind ?? 'all');
      button.classList.toggle('on', active);
      /* ⚠️ Not colour alone (Design/00 §4, V7) — the pressed state is in the
         accessibility tree too, so it is not a purely visual signal. */
      button.setAttribute('aria-pressed', String(active));
    }
  }

  private queueSearch(): void {
    if (this.debounce !== null) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.runSearch(), SEARCH_DEBOUNCE_MS);
  }

  private runSearch(): void {
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }

    const query = this.searchInput.value.trim();
    if (query.length < 2) {
      this.resultList.replaceChildren();
      return;
    }

    /* ⚠️ Generation-guarded, exactly like the connection in `main.ts`. Two
       searches in flight can return out of order, and the older one landing
       last would replace the results for what you actually typed. */
    const generation = ++this.searchGeneration;

    this.handlers
      .search(query, this.kind)
      .then((hits) => {
        if (generation !== this.searchGeneration) return;
        this.showResults(hits);
      })
      .catch(() => {
        if (generation !== this.searchGeneration) return;
        this.showMessage('Search unavailable — is the search index built?');
      });
  }

  private showMessage(text: string): void {
    const message = document.createElement('div');
    message.className = 'devtools-empty';
    message.textContent = text;
    this.resultList.replaceChildren(message);
  }

  /**
   * ⚠️ Built from DOM nodes, never `innerHTML`.
   *
   * Every string here — street names, örnefni, `nafnberi` — is text somebody
   * else authored, arriving from the geodata database. `textContent` cannot
   * execute anything whatever is in it, which is a stronger guarantee than
   * remembering to escape. Same rule as the HUD's street labels.
   */
  private showResults(hits: readonly SearchHit[]): void {
    if (hits.length === 0) {
      this.showMessage('Nothing found');
      return;
    }

    const rows = hits.map((hit) => {
      const row = document.createElement('div');
      row.className = hit.inWorld ? 'devtools-hit' : 'devtools-hit outside';

      const text = document.createElement('div');
      text.className = 'devtools-hit-text';

      const label = document.createElement('b');
      label.textContent = hit.label;

      /**
       * ⚠️ The town goes on the **first** line, beside the name.
       *
       * haugur searched "hverfisgata" and got two "Hverfisgata 3" he could not
       * tell apart. The distinguishing fact has to sit next to the thing it
       * distinguishes — buried on a second line among identifiers and
       * coordinates it is not doing the job it exists for.
       */
      const town = document.createElement('span');
      town.className = 'devtools-town';
      town.textContent = hit.town === '' ? '' : `, ${hit.town}`;

      const kind = document.createElement('span');
      kind.className = 'devtools-kind';
      kind.textContent = KIND_LABEL[hit.kind];

      const detail = document.createElement('span');
      detail.className = 'devtools-detail';
      /* The identifiers matter as much as the name here — a landnúmer you
         pasted should visibly be the thing that matched. */
      detail.textContent = [hit.detail, hit.refs].filter((s) => s !== '').join(' · ');

      const where = document.createElement('span');
      where.className = 'devtools-where';
      where.textContent = hit.inWorld
        ? `${hit.x.toFixed(0)}, ${hit.y.toFixed(0)}`
        : `${hit.x.toFixed(0)}, ${hit.y.toFixed(0)} · outside the world`;

      text.append(label, town, kind, document.createElement('br'), detail, where);

      const go = document.createElement('button');
      go.className = 'devtools-go';
      go.type = 'button';
      go.textContent = 'Go';
      go.addEventListener('click', () => {
        this.handlers.onTeleport(hit.x, hit.y, hit.label);
      });

      row.append(text, go);
      return row;
    });

    this.resultList.replaceChildren(...rows);
  }
}

/**
 * Is this ISN93 point inside the world that actually exists?
 *
 * 🔴 **The world is all of Iceland since T193**, so this asks about the DEM's
 * extent, not about `TEST_AREA`. ⚠️ **It used to ask about the box**, and the
 * name said `insideTestArea` — which was true then and would have quietly
 * answered the wrong question the day the world opened, marking the whole
 * country as ground that does not exist.
 */
export function insideWorld(x: number, northing: number): boolean {
  return inWorld(x, northingToZ(northing));
}

/**
 * A compass point for a bearing, so "168°" also reads as "S".
 *
 * ⚠️ North is **−z** in world space (`northingToZ` is `-northing`), so a
 * bearing `a` points along `(sin a, −cos a)`. Iceland's midday sun is due
 * south — 180° — and the shipped default of 122° puts it in the east-southeast.
 */
export function compass(degrees: number): string {
  const points = [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW',
  ] as const;
  const index = Math.round((((degrees % 360) + 360) % 360) / 22.5) % 16;
  /* `.at` rather than `[index]` — a computed index into an array is what
     `security/detect-object-injection` flags, and `.at` says the same thing
     without the sink. */
  return points.at(index) ?? 'N';
}

/** One fold's contents. Hidden until its header is clicked. */
function foldBody(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'devtools-fold';
  el.hidden = true;
  return el;
}

function section(title: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'devtools-section';
  el.textContent = title;
  return el;
}
